import React from 'react';
import PropTypes from 'prop-types';

/**
 * Logos de navegador para la pestaña Sesiones del Perfil, como un sprite SVG.
 *
 * Son marcas simplificadas dibujadas a mano, no los assets oficiales: a 16px lo
 * que identifica a cada navegador es el color y la forma gruesa, asi que no hace
 * falta el artwork exacto ni una dependencia que lo traiga. Geometria basica —
 * circulos, elipses y tres sectores para el de Chrome.
 *
 * Van como <symbol> y no inline en cada fila para que el dibujo se defina una
 * sola vez por mas sesiones que haya listadas.
 *
 * El slug con el que se elige cada uno lo manda el backend en el campo
 * `navegador` (users/services/services_dispositivo.py). Que el logo salga de
 * aca, de font-awesome o de ningun lado es decision de este archivo: el backend
 * solo dice que navegador es.
 */


// Logos en viewBox 16x16, por el slug que manda el backend.
//
// Los dos que son "un disco con una media luna mas clara" (Firefox y Edge) se
// arman con dos circulos y un clip, en vez de con arcos: el circulo de arriba,
// corrido al costado, deja la media luna del otro lado. Es menos elegante que un
// path pero no hay forma de equivocarle la curvatura, y recortado al disco no se
// puede desbordar.
const LOGOS = {
    chrome: (
        <g>
            {/* Tres sectores de 120 grados: rojo arriba, amarillo a la derecha y
                abajo, verde abajo y a la izquierda. */}
            <path d="M8 8 L1.07 4 A8 8 0 0 1 14.93 4 Z" fill="#ea4335" />
            <path d="M8 8 L14.93 4 A8 8 0 0 1 8 16 Z" fill="#fbbc04" />
            <path d="M8 8 L8 16 A8 8 0 0 1 1.07 4 Z" fill="#34a853" />
            <circle cx="8" cy="8" r="4.1" fill="#ffffff" />
            <circle cx="8" cy="8" r="3.2" fill="#4285f4" />
        </g>
    ),
    firefox: (
        <g clipPath="url(#ic-nav-disco)">
            <circle cx="8" cy="8" r="8" fill="#ffcb0e" />
            <circle cx="10.6" cy="7.4" r="7.6" fill="#ff7139" />
        </g>
    ),
    edge: (
        // La media luna va a la derecha, al reves que la de Firefox: son la
        // misma figura y sin eso se distinguirian solo por el color.
        <g clipPath="url(#ic-nav-disco)">
            <circle cx="8" cy="8" r="8" fill="#37c6f4" />
            <circle cx="5.4" cy="8.6" r="7.6" fill="#0f5c9c" />
        </g>
    ),
    safari: (
        <g>
            <circle cx="8" cy="8" r="8" fill="#19a0ff" />
            <circle cx="8" cy="8" r="6.3" fill="#ffffff" />
            {/* La aguja: dos triangulos opuestos sobre la diagonal NE-SO. */}
            <path d="M11.6 4.4 L8.78 8.78 L7.22 7.22 Z" fill="#ff3b30" />
            <path d="M4.4 11.6 L8.78 8.78 L7.22 7.22 Z" fill="#c7c7cc" />
        </g>
    ),
    opera: (
        <g>
            <circle cx="8" cy="8" r="8" fill="#ff1b2d" />
            <ellipse cx="8" cy="8" rx="2.7" ry="5.1" fill="#ffffff" />
        </g>
    ),
    samsung: (
        // Un anillo y no una media luna como los otros dos: la de Samsung seria
        // blanca sobre el fondo blanco de la tabla, o sea invisible.
        <g>
            <circle cx="8" cy="8" r="8" fill="#1428a0" />
            <circle
                cx="8"
                cy="8"
                r="4.6"
                fill="none"
                stroke="#ffffff"
                strokeWidth="1.5"
            />
        </g>
    ),
    // Para los navegadores que el backend reconoce pero no tienen logo aca. No es
    // el mismo caso que no reconocerlo: ahi no se dibuja nada.
    generico: (
        <g>
            <circle cx="8" cy="8" r="8" fill="#8a93a2" />
            <g fill="none" stroke="#ffffff" strokeWidth="1.1">
                <circle cx="8" cy="8" r="5.6" />
                <ellipse cx="8" cy="8" rx="2.6" ry="5.6" />
                <path d="M2.4 8 L13.6 8" />
            </g>
        </g>
    ),
};


/**
 * El sprite en si: hay que montarlo UNA vez donde se usen los logos.
 *
 * Va con width y height en 0 y posicion absoluta porque no se ve: solo define los
 * <symbol> que despues referencian los <use> de <LogoNavegador>. Si no esta
 * montado, los logos quedan en blanco.
 */
export function SpriteSesiones() {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="0"
            height="0"
            style={{ position: 'absolute', overflow: 'hidden' }}
            aria-hidden="true"
        >
            <defs>
                <clipPath id="ic-nav-disco">
                    <circle cx="8" cy="8" r="8" />
                </clipPath>
            </defs>
            {Object.keys(LOGOS).map((slug) =>
                <symbol key={slug} id={'ic-nav-' + slug} viewBox="0 0 16 16">
                    {LOGOS[slug]}
                </symbol>
            )}
        </svg>
    );
}


/**
 * El logo del navegador, por el slug que manda el backend en `navegador`.
 *
 * Sin slug no dibuja nada: el backend no reconocio el User-Agent y un logo
 * inventado seria peor que ninguno. Con un slug sin logo propio va el generico.
 */
export function LogoNavegador({ navegador }) {
    if (!navegador) return null;

    const clave = LOGOS[navegador] ? navegador : 'generico';

    return (
        <svg
            width="14"
            height="14"
            className="mr-1"
            style={{ verticalAlign: 'text-bottom' }}
            aria-hidden="true"
        >
            <use xlinkHref={'#ic-nav-' + clave} />
        </svg>
    );
}

LogoNavegador.propTypes = {
    navegador: PropTypes.string,
};



// WEBPACK FOOTER //
// ./src/components/IconosSesiones/IconosSesiones.js